import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { ChevronLeft, FlaskConical, Pencil, Share2, GitBranch } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useLeagueStrict } from "@/lib/league-context";
import {
  getScenario,
  renameScenario,
  getScenarioLeaderboard,
  getScenarioEditorMeta,
  listScenarioShareTargets,
  shareScenario,
  unshareScenario,
} from "@/lib/scenarios.functions";
import { NewScenarioButton } from "@/components/NewScenarioButton";
import { ScenarioBracketEditor } from "@/components/scenarios/ScenarioBracketEditor";
import { ScenarioBonusToggleCard } from "@/components/scenarios/ScenarioBonusToggleCard";
import { ScenarioTiebreakerCard } from "@/components/scenarios/ScenarioTiebreakerCard";
import { toast } from "sonner";

export const Route = createFileRoute("/_app/$league/scenarios/$scenarioId")({
  component: ScenarioEditorPage,
});

function ScenarioEditorPage() {
  const { league, scenarioId } = Route.useParams();
  const leagueCtx = useLeagueStrict();
  const qc = useQueryClient();
  const get = useServerFn(getScenario);
  const rename = useServerFn(renameScenario);
  const getLb = useServerFn(getScenarioLeaderboard);

  const { data, isLoading, error } = useQuery({
    queryKey: ["scenario", scenarioId],
    queryFn: () => get({ data: { scenarioId } }),
  });

  const lbQuery = useQuery({
    queryKey: ["scenario-leaderboard", scenarioId, data?.updatedAt ?? null],
    queryFn: () => getLb({ data: { scenarioId } }),
    enabled: !!data,
  });

  const [name, setName] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (data?.name) setName(data.name);
  }, [data?.name]);

  // Auto-focus and select all when the scenario still has the default name
  useEffect(() => {
    if (data?.name === "Untitled scenario" && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [data?.name]);

  // Fire once per scenario open.
  useEffect(() => {
    if (!data) return;
    void import("@/lib/analytics").then(({ track }) =>
      track("scenario_viewed", {
        leagueId: leagueCtx.id,
        leagueSlug: league,
        scenarioId,
        isOwner: data.isCreator,
        isFork: Boolean(data.forkedFromScenarioId),
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioId, data?.isCreator]);

  const renameMut = useMutation({
    mutationFn: (newName: string) =>
      rename({ data: { scenarioId, name: newName } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["scenarios", leagueCtx.id] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-sm text-muted-foreground">
        Loading scenario…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-12 text-center">
        <h1 className="text-xl font-semibold mb-2">Scenario not found</h1>
        <p className="text-sm text-muted-foreground mb-4">
          {error?.message ?? "It may have been deleted."}
        </p>
        <Link
          to="/$league/scenarios"
          params={{ league }}
          className="text-sm underline"
        >
          Back to scenarios
        </Link>
      </div>
    );
  }

  const readOnly = !data.isCreator;

  return (
    <div className="max-w-4xl mx-auto px-4 pb-12">
      {/* Sticky scenario-mode banner */}
      <div className="sticky top-[52px] z-20 -mx-4 px-4 py-2 bg-amber-500/10 border-b border-amber-500/30 backdrop-blur">
        <div className="flex items-center gap-2 text-xs font-semibold text-amber-700 dark:text-amber-400">
          <FlaskConical className="w-4 h-4" />
          <span>SCENARIO MODE</span>
          <span className="font-normal text-muted-foreground">
            · sandbox · doesn't affect your real picks
          </span>
        </div>
      </div>

      <div className="pt-6 space-y-6">
        <div className="flex items-center gap-2">
          <Link
            to="/$league/scenarios"
            params={{ league }}
            className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center"
          >
            <ChevronLeft className="w-4 h-4" />
            All scenarios
          </Link>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {readOnly ? (
            <>
              <h1 className="text-2xl font-bold">{data.name}</h1>
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-semibold">
                Read-only
              </span>
              <div className="ml-auto">
                <NewScenarioButton
                  mode="fork"
                  leagueId={leagueCtx.id}
                  leagueSlug={leagueCtx.slug}
                  forkedFromScenarioId={scenarioId}
                  forkedFromName={data.name}
                />
              </div>
            </>

          ) : (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <Input
                ref={inputRef}
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => {
                  const trimmed = name.trim();
                  if (trimmed && trimmed !== data.name) {
                    renameMut.mutate(trimmed);
                  }
                }}
                maxLength={80}
                className={cn(
                  "text-2xl font-bold h-auto py-1",
                  data?.name === "Untitled scenario"
                    ? "border-input ring-2 ring-primary/20"
                    : "border-transparent hover:border-border focus:border-input",
                )}
                placeholder="Untitled scenario"
              />
              <button
                type="button"
                onClick={() => inputRef.current?.focus()}
                className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                title="Rename scenario"
              >
                <Pencil className="w-4 h-4" />
              </button>
              {renameMut.isPending && (
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  Saving…
                </span>
              )}
            </div>
          )}
          {!readOnly && <ShareScenarioDialog scenarioId={scenarioId} />}
        </div>

        {data.forkedFromCreatorSnapshot && (
          <div className="flex items-center gap-1.5 -mt-4">
            <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20">
              <GitBranch className="w-3 h-3" />
              Forked from {data.forkedFromCreatorSnapshot}
            </span>
          </div>
        )}

        <ScenarioEditorBody
          scenarioId={scenarioId}
          readOnly={readOnly}
          overrides={data.overrides ?? []}
          onChanged={() =>
            void qc.invalidateQueries({ queryKey: ["scenario", scenarioId] })
          }
        />




        {/* Live mock leaderboard */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Mock leaderboard
            </h2>
            {lbQuery.isFetching && (
              <span className="text-xs text-muted-foreground">Recomputing…</span>
            )}
          </div>
          {lbQuery.isLoading ? (
            <div className="p-6 text-sm text-muted-foreground text-center">
              Computing scenario…
            </div>
          ) : lbQuery.error ? (
            <div className="p-6 text-sm text-destructive text-center">
              {(lbQuery.error as Error).message}
            </div>
          ) : lbQuery.data ? (
            <>
              {(() => {
                const myEntryIds = new Set<string>([
                  ...(data.forkedFromEntryId ? [data.forkedFromEntryId] : []),
                  ...(lbQuery.data.myEntryIds ?? []),
                ]);
                const youIdx = lbQuery.data.rows.findIndex((r) =>
                  myEntryIds.has(r.entryId),
                );
                if (youIdx < 0) return null;
                const you = lbQuery.data.rows[youIdx];
                const total = lbQuery.data.rows.length;
                const leader = lbQuery.data.rows[0];
                const gap = leader ? leader.totalPoints - you.totalPoints : 0;
                const youRank = you.rank ?? youIdx + 1;
                return (
                  <div className="px-4 py-3 border-b border-border bg-primary/10 flex items-center gap-3">
                    <div className="shrink-0 w-11 h-11 rounded-lg flex flex-col items-center justify-center bg-primary text-primary-foreground font-display font-bold shadow-[var(--shadow-neon)]">
                      <span className="text-[9px] uppercase tracking-wider opacity-80 leading-none">
                        You
                      </span>
                      <span className="text-base leading-none mt-0.5">
                        #{youRank}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold truncate text-sm">
                        {you.bracketName || "Your bracket"}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        Rank {youRank} of {total}
                        {youRank > 1 && ` · ${gap} pt${gap === 1 ? "" : "s"} behind leader`}
                        {youRank === 1 && " · 🥇 in the lead"}
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="font-display font-bold text-xl text-primary tabular-nums leading-none">
                        {you.totalPoints}
                        {you.totalPossiblePoints > you.totalPoints && (
                          <span className="text-muted-foreground font-normal text-sm">
                            {" / "}{you.totalPossiblePoints}
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-1">
                        pts{you.totalPossiblePoints > you.totalPoints ? " · possible" : ""}
                      </div>
                    </div>
                  </div>
                );
              })()}
              <ul className="divide-y divide-border">
                {lbQuery.data.rows.map((r, i) => {
                  const myEntryIds = new Set<string>([
                    ...(data.forkedFromEntryId ? [data.forkedFromEntryId] : []),
                    ...(lbQuery.data!.myEntryIds ?? []),
                  ]);
                  const isYou = myEntryIds.has(r.entryId);


                  return (
                    <li
                      key={r.entryId}
                      className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                        isYou
                          ? "bg-primary/10 ring-1 ring-inset ring-primary/40 border-l-4 border-l-primary"
                          : ""
                      }`}
                    >
                      <span
                        className={`w-8 text-right tabular-nums font-medium ${
                          isYou ? "text-primary font-bold" : "text-muted-foreground"
                        }`}
                      >
                        {r.rank ?? i + 1}
                      </span>
                      <span className="flex-1 truncate flex items-center gap-2">
                        {isYou && <span aria-hidden>👉</span>}
                        <span className={isYou ? "font-semibold" : ""}>
                          {r.bracketName}
                        </span>
                        {isYou && (
                          <span className="ml-1 inline-flex items-center rounded-full bg-primary text-primary-foreground px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold shadow-[var(--shadow-neon)]">
                            You
                          </span>
                        )}
                      </span>
                      <span
                        className={`tabular-nums font-semibold ${
                          isYou ? "text-primary" : ""
                        }`}
                      >
                        {r.totalPoints}
                        {r.totalPossiblePoints > r.totalPoints && (
                          <span className="text-muted-foreground font-normal">
                            {" / "}{r.totalPossiblePoints}
                          </span>
                        )}
                      </span>
                    </li>
                  );
                })}
                {lbQuery.data.rows.length === 0 && (
                  <li className="p-6 text-sm text-muted-foreground text-center">
                    No entries yet.
                  </li>
                )}
              </ul>
            </>
          ) : null}

        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Scenario editor body — bracket + bonus + tiebreaker
// =============================================================================

type OverrideLite = {
  id: string;
  kind: string;
  refId: string;
  payload: Record<string, string | number | boolean | string[] | null>;
};

function ScenarioEditorBody({
  scenarioId,
  readOnly,
  overrides,
  onChanged,
}: {
  scenarioId: string;
  readOnly: boolean;
  overrides: OverrideLite[];
  onChanged: () => void;
}) {
  const getMeta = useServerFn(getScenarioEditorMeta);
  const metaQ = useQuery({
    queryKey: ["scenario-editor-meta", scenarioId],
    queryFn: () => getMeta({ data: { scenarioId } }),
  });

  // Champion is "set" when the FINAL match has either an actual result or an
  // actual_knockout_result override picking a winner.
  const finalOverride = overrides.find(
    (o) => o.kind === "actual_knockout_result" && o.refId === "FINAL",
  );
  const overrideChampion =
    finalOverride && typeof finalOverride.payload.winner_team_id === "string"
      ? (finalOverride.payload.winner_team_id as string)
      : null;
  const championSet =
    !!overrideChampion || !!metaQ.data?.actualFinalWinnerTeamId;

  return (
    <>
      <ScenarioBracketEditor
        scenarioId={scenarioId}
        readOnly={readOnly}
        overrides={overrides}
        onChanged={onChanged}
      />
      <ScenarioBonusToggleCard
        scenarioId={scenarioId}
        readOnly={readOnly}
        overrides={overrides}
        onChanged={onChanged}
      />
      <ScenarioTiebreakerCard
        scenarioId={scenarioId}
        readOnly={readOnly}
        overrides={overrides}
        championSet={championSet}
        myGuess={metaQ.data?.myTiebreakerGuess ?? null}
      />
    </>
  );
}


// =============================================================================
// Share dialog
// =============================================================================

function ShareScenarioDialog({ scenarioId }: { scenarioId: string }) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const list = useServerFn(listScenarioShareTargets);
  const share = useServerFn(shareScenario);
  const unshare = useServerFn(unshareScenario);

  const targetsQ = useQuery({
    queryKey: ["scenario-share-targets", scenarioId],
    queryFn: () => list({ data: { scenarioId } }),
    enabled: open,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["scenario-share-targets", scenarioId] });
  };

  const shareMut = useMutation({
    mutationFn: (userId: string) =>
      share({ data: { scenarioId, shareWithUserId: userId } }),
    onSuccess: (_data, userId) => {
      invalidate();
      toast.success("Shared");
      void import("@/lib/analytics").then(({ track }) =>
        track("scenario_shared", { scenarioId, sharedWithUserId: userId }),
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const unshareMut = useMutation({
    mutationFn: (userId: string) =>
      unshare({ data: { scenarioId, shareWithUserId: userId } }),
    onSuccess: (_data, userId) => {
      invalidate();
      void import("@/lib/analytics").then(({ track }) =>
        track("scenario_unshared", { scenarioId, unsharedUserId: userId }),
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const [filter, setFilter] = useState("");
  const filtered =
    targetsQ.data?.members.filter((m) =>
      m.name.toLowerCase().includes(filter.toLowerCase()),
    ) ?? [];

  const sharedCount = targetsQ.data?.members.filter((m) => m.isShared).length ?? 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <Share2 className="w-4 h-4" />
          Share
          {sharedCount > 0 && (
            <span className="text-xs text-muted-foreground">({sharedCount})</span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Share scenario</DialogTitle>
          <DialogDescription>
            Pick league members who can view this sandbox. They can see your
            overrides and the mock leaderboard but can't edit anything.
          </DialogDescription>
        </DialogHeader>

        <Input
          placeholder="Search members…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />

        <div className="max-h-80 overflow-y-auto -mx-6 px-6 divide-y divide-border">
          {targetsQ.isLoading && (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Loading members…
            </p>
          )}
          {targetsQ.data && filtered.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No members match.
            </p>
          )}
          {filtered.map((m) => (
            <div
              key={m.id}
              className="flex items-center justify-between gap-3 py-2"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{m.name}</div>
              </div>
              {m.isShared ? (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={unshareMut.isPending}
                  onClick={() => unshareMut.mutate(m.id)}
                >
                  Remove
                </Button>
              ) : (
                <Button
                  size="sm"
                  disabled={shareMut.isPending}
                  onClick={() => shareMut.mutate(m.id)}
                >
                  Share
                </Button>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

